import { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { CopyButton } from '../components/ui/copy-button';
import { useToast } from '../components/ui/toast';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import {
  ApiError,
  createUser,
  getUsers,
  resetUserPassword,
  updateUser,
  type AdminUserRole,
  type UserRow,
} from './users.api';

const ROLE_LABELS: Record<AdminUserRole, string> = { admin: 'Admin', employee: 'Employee' };
const ROLE_OPTIONS = Object.entries(ROLE_LABELS) as [AdminUserRole, string][];

function toRole(value: string): AdminUserRole {
  return value === 'employee' ? 'employee' : 'admin';
}

/** One error line from anything this screen can throw (the credentials idiom). */
function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const fieldErrors = err.validationErrors
      ? Object.values(err.validationErrors).flat().join(' ')
      : '';
    return [err.message, fieldErrors].filter(Boolean).join(': ') || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** The lockout cell's text: "Locked until <local datetime>" while locked, else the em dash. */
function lockoutLabel(lockedUntil: string | null): string {
  if (lockedUntil === null) return '—';
  const expiry = new Date(lockedUntil);
  return expiry.getTime() > Date.now() ? `Locked until ${expiry.toLocaleString()}` : '—';
}

/** The reset dialog's post-success view: the target's name beside the reveal. */
interface UserRowWithGeneratedPasswordView {
  displayName: string;
  password: string;
}

/**
 * The one-time generated-password reveal (epic 103 D3): the api returns the
 * plaintext exactly once, so the dialog that triggered the write swaps to
 * this panel — monospace value, copy button, and the "shown only once"
 * warning. The value lives only in this component's state, never storage.
 */
function GeneratedPasswordPanel({ password }: { password: string }) {
  return (
    <div className="rounded-md border border-accent/40 bg-accent-soft/40 p-4">
      <div className="flex items-center gap-3">
        <code className="min-w-0 flex-1 break-all rounded bg-surface px-3 py-2 font-mono text-sm font-semibold text-ink">
          {password}
        </code>
        <CopyButton value={password} label="Copy" title="Copy the generated password" />
      </div>
      <p className="mt-2 text-xs text-muted">
        Shown only once — copy it now and hand it to the user. They must set a new password at first
        sign-in.
      </p>
    </div>
  );
}

interface CreateForm {
  email: string;
  displayName: string;
  role: AdminUserRole;
  password: string;
}

const emptyCreateForm: CreateForm = {
  email: '',
  displayName: '',
  role: 'employee',
  password: '',
};

interface EditForm {
  displayName: string;
  role: AdminUserRole;
  isActive: boolean;
}

function editFormOf(row: UserRow): EditForm {
  return { displayName: row.displayName, role: row.role, isActive: row.isActive };
}

/**
 * User management (epic 103 D3/D8, task 03; admin-only — the route itself is
 * `RequireAdmin`-gated): list / create / edit / reset password. The
 * generated-password reveal is the one-time hand-off for both create and
 * reset; every mutation ends in a toast + a list refresh.
 */
export function Users() {
  const { user: actingUser } = useAuth();
  const { success } = useToast();

  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [denied, setDenied] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(emptyCreateForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);

  const [editing, setEditing] = useState<UserRow | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [resetting, setResetting] = useState<UserRow | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resettingBusy, setResettingBusy] = useState(false);
  const [resetResult, setResetResult] = useState<UserRowWithGeneratedPasswordView | null>(null);

  const loadRows = useCallback(() => {
    getUsers()
      .then((list) => {
        setRows(list);
        setDenied(false);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          setDenied(true);
          return;
        }
        setLoadError(describeError(err, 'Failed to load users'));
      });
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openCreate = () => {
    setCreateForm(emptyCreateForm);
    setCreateError(null);
    setCreatedPassword(null);
    setIsCreateOpen(true);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createUser({
        email: createForm.email.trim(),
        displayName: createForm.displayName.trim(),
        role: createForm.role,
        ...(createForm.password.length > 0 ? { password: createForm.password } : {}),
      });
      success(`User "${result.user.displayName}" created`);
      if (result.generatedPassword !== null) {
        setCreatedPassword(result.generatedPassword);
      } else {
        setIsCreateOpen(false);
        setCreateForm(emptyCreateForm);
      }
      loadRows();
    } catch (err) {
      setCreateError(describeError(err, 'Failed to create the user'));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (row: UserRow) => {
    setEditing(row);
    setEditForm(editFormOf(row));
    setEditError(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(null);
    setEditError(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing === null || editForm === null) return;
    setSaving(true);
    setEditError(null);
    try {
      const selfEdit = editing.id === actingUser?.id;
      const row = await updateUser(editing.id, {
        displayName: editForm.displayName.trim(),
        // The self row's role/status inputs are disabled (the api's 400
        // self-guard fires on the KEYS, not the values) — a self-rename
        // sends the displayName only.
        ...(selfEdit ? {} : { role: editForm.role, isActive: editForm.isActive }),
      });
      success(`User "${row.displayName}" saved`);
      closeEdit();
      loadRows();
    } catch (err) {
      setEditError(describeError(err, 'Failed to save the user'));
    } finally {
      setSaving(false);
    }
  };

  const openReset = (row: UserRow) => {
    setResetting(row);
    setResetPassword('');
    setResetError(null);
    setResetResult(null);
  };

  const closeReset = () => {
    setResetting(null);
    setResetPassword('');
    setResetError(null);
    setResetResult(null);
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetting === null) return;
    setResettingBusy(true);
    setResetError(null);
    try {
      const result = await resetUserPassword(
        resetting.id,
        resetPassword.length > 0 ? { newPassword: resetPassword } : {}
      );
      success(`Password reset for "${result.user.displayName}"`);
      if (result.generatedPassword !== null) {
        setResetResult({
          displayName: result.user.displayName,
          password: result.generatedPassword,
        });
        setResetPassword('');
      } else {
        closeReset();
      }
      loadRows();
    } catch (err) {
      setResetError(describeError(err, 'Failed to reset the password'));
    } finally {
      setResettingBusy(false);
    }
  };

  if (denied) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md rounded-lg border border-line bg-card p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">Access denied</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            You don't have access to user management.
          </p>
        </div>
      </div>
    );
  }

  if (loadError !== null) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading users: {loadError}
        </div>
      </div>
    );
  }

  if (rows === null) {
    return (
      <div role="status" className="text-sm text-muted">
        Loading users…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Users</h1>
          <p className="text-muted-foreground mt-2">
            Admin accounts with full access, and employees who see everything except user
            management.
          </p>
        </div>
        <Button onClick={openCreate}>Add user</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Lockout</TableHead>
                <TableHead>Must change</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No users yet. Add one to give someone access to the console.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const self = actingUser !== null && actingUser.id === row.id;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.displayName}
                        {self && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">{row.email}</TableCell>
                      <TableCell>
                        <span
                          className={
                            row.role === 'admin'
                              ? 'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide bg-accent-soft text-accent'
                              : 'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide bg-line/60 text-muted'
                          }
                        >
                          {ROLE_LABELS[row.role]}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.isActive ? 'Active' : 'Inactive'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {lockoutLabel(row.lockedUntil)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.mustChangePassword ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {fmtDate(row.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={self}
                          title={self ? 'Use Change password for your own password' : undefined}
                          onClick={() => openReset(row)}
                        >
                          Reset password
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={(open) => !open && setIsCreateOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              The user signs in with this login and must set a new password at first sign-in.
            </DialogDescription>
          </DialogHeader>
          {createdPassword !== null ? (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                User created. Their generated password:
              </p>
              <GeneratedPasswordPanel password={createdPassword} />
              <DialogFooter>
                <Button type="button" onClick={() => setIsCreateOpen(false)}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreateSubmit}>
              <div className="space-y-4 py-4">
                {createError && <p className="text-sm text-destructive">{createError}</p>}
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email</Label>
                  <Input
                    id="user-email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    required
                    placeholder="e.g., name@example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    The login name — any single word works, stored lowercase.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-display-name">Display name</Label>
                  <Input
                    id="user-display-name"
                    value={createForm.displayName}
                    onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })}
                    required
                    placeholder="e.g., Chelsie"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-role">Role</Label>
                  <Select
                    id="user-role"
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: toRole(e.target.value) })}
                  >
                    {ROLE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Admins manage users too; employees see everything except user management.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password">Password (optional)</Label>
                  <Input
                    id="user-password"
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="Leave empty to generate one"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty and the server generates a password, shown once after saving — a
                    change is forced at first sign-in either way.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating…' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {editing !== null && editForm !== null && (
        <Dialog open onOpenChange={(open) => !open && closeEdit()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit user</DialogTitle>
              <DialogDescription>
                {editing.email} — created {fmtDate(editing.createdAt)}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit}>
              <div className="space-y-4 py-4">
                {editError && <p className="text-sm text-destructive">{editError}</p>}
                <div className="space-y-2">
                  <Label htmlFor="edit-display-name">Display name</Label>
                  <Input
                    id="edit-display-name"
                    value={editForm.displayName}
                    onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <Select
                    id="edit-role"
                    value={editForm.role}
                    disabled={editing.id === actingUser?.id}
                    onChange={(e) => setEditForm({ ...editForm, role: toRole(e.target.value) })}
                  >
                    {ROLE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  {editing.id === actingUser?.id && (
                    <p className="text-xs text-muted-foreground">
                      You cannot change your own role or status.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-active">Active</Label>
                  <Select
                    id="edit-active"
                    value={editForm.isActive ? 'active' : 'inactive'}
                    disabled={editing.id === actingUser?.id}
                    onChange={(e) =>
                      setEditForm({ ...editForm, isActive: e.target.value === 'active' })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeEdit}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={editing.id === actingUser?.id || resettingBusy}
                  title={
                    editing.id === actingUser?.id
                      ? 'Use Change password (account menu) for your own password'
                      : undefined
                  }
                  onClick={() => {
                    const row = editing;
                    closeEdit();
                    openReset(row);
                  }}
                >
                  Reset password…
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {resetting !== null && (
        <Dialog open onOpenChange={(open) => !open && closeReset()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset password</DialogTitle>
              <DialogDescription>
                {resetting.displayName} ({resetting.email}) — their sessions are signed out and a
                password change is forced.
              </DialogDescription>
            </DialogHeader>
            {resetResult !== null ? (
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Password reset for {resetResult.displayName}. The new generated password:
                </p>
                <GeneratedPasswordPanel password={resetResult.password} />
                <DialogFooter>
                  <Button type="button" onClick={closeReset}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={handleResetSubmit}>
                <div className="space-y-4 py-4">
                  {resetError && <p className="text-sm text-destructive">{resetError}</p>}
                  <div className="space-y-2">
                    <Label htmlFor="reset-password">New password (optional)</Label>
                    <Input
                      id="reset-password"
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Leave empty to generate one"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty and the server generates a password, shown once after saving.
                      {resetting.id === actingUser?.id
                        ? ' Your own password changes go through Change password.'
                        : ''}
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeReset}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={resettingBusy}>
                    {resettingBusy ? 'Resetting…' : 'Reset password'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
